// src/drawing/tools/PointTool.js
import proj4 from "proj4";
import { getUtmProj } from "../utils/projection";

export class PointTool {
  constructor(map, getFirstABVector, getGeometries, setGeometries) {
    this.map = map;

    this.getFirstABVector = getFirstABVector;
    this.getGeometries = getGeometries;
    this.setGeometries = setGeometries;

    this.selectedIndex = null;
    this.dragMode = null;

    this.rect = null;
    this.layoutPoints = null;

    // Undo-Verlauf (Signify-Feedback Dennis 03.07.2026): beim ANWÄHLEN
    // eines Mastes wird die aktuelle Punktlage gesichert, damit
    // "Rückgängig" die letzte Verschiebung zurücknehmen kann.
    this.history = [];

    // Warnbanner (Frontend-Fail-safe, 03.07.2026): erscheint, sobald ein
    // Mast im gezeichneten Spielfeld liegt. Das Backend bricht solche
    // Projekte beim Rechnen ohnehin ab (project_validation.py) — die
    // Warnung hier fängt den Fehler schon beim Zeichnen ab.
    this.fieldWarning = document.createElement("div");
    this.fieldWarning.style.cssText = `
      position: absolute;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 6;
      padding: 6px 14px;
      background: #fef2f2;
      border: 2px solid #dc2626;
      border-radius: 6px;
      color: #dc2626;
      font-family: system-ui, sans-serif;
      font-size: 13px;
      font-weight: 600;
      white-space: nowrap;
      pointer-events: none;
      display: none;
      box-shadow: 0 2px 6px rgba(0,0,0,0.15);
    `;
    this.map.getCanvas().parentElement.appendChild(this.fieldWarning);
  }

  // Zeigt/versteckt das Banner je nachdem, ob Masten im Feld liegen.
  // Wird bei jeder Punktänderung aufgerufen (platzieren, ziehen, undo).
  updateFieldWarning(points) {
    const rect = this.getGeometries()?.fieldRectangle;
    if (!rect || !points?.length) {
      this.fieldWarning.style.display = "none";
      return;
    }
    const anzahl = points.filter((p) => pointInPolygon(p, rect)).length;
    if (anzahl > 0) {
      this.fieldWarning.textContent =
        anzahl > 1
          ? `⚠ ${anzahl} Masten liegen im Spielfeld — bitte nach außen ziehen`
          : "⚠ Mast liegt im Spielfeld — bitte nach außen ziehen";
      this.fieldWarning.style.display = "block";
    } else {
      this.fieldWarning.style.display = "none";
    }
  }

  placePoints(rect, layoutPoints) {

    this.rect = rect;
    this.layoutPoints = layoutPoints;

    const points = layoutPoints.map((pt) => {
      const start = rect[pt.edge];
      const end = rect[(pt.edge + 1) % 4];
      return [
        start[0] + pt.t * (end[0] - start[0]),
        start[1] + pt.t * (end[1] - start[1]),
      ];
    });

    this.setGeometries((prev) => ({ ...prev, mastPoints: points }));

    // Versatz je Mast EINMAL in der Preset-Lage messen (siehe placeArrows).
    this._captureAngleOffsets(points);

    const arrows = this.placeArrows(points);
    this.updateFieldWarning(points);
  }

  // Misst für jeden Mast, um wie viel seine Preset-Blickrichtung von der
  // Richtung "zur Feldmitte" abweicht, und merkt sich das als angleOffset.
  //
  // Dadurch bleibt die AUSRICHTUNGSABSICHT des Layouts erhalten: ein Eckmast
  // mit Layout-Winkel 45° zielt bewusst neben die Feldmitte, um seinen
  // Quadranten auszuleuchten. Würden wir stumpf alle Masten auf die Mitte
  // richten, ginge genau diese Verteilung verloren und wir hätten dasselbe
  // Problem an anderer Stelle.
  //
  // In der Preset-Lage ergibt centreDir + angleOffset EXAKT die alte
  // Richtung — Bestandsprojekte rechnen also unverändert. Erst wenn ein Mast
  // verschoben wird, dreht sich die Basis mit, und der Versatz bleibt.
  _captureAngleOffsets(points) {
    if (!this.rect || !this.layoutPoints) return;

    const wgs84 = "EPSG:4326";
    const utm = getUtmProj(this.rect[0]);
    const rectUTM = this.rect.map((p) => proj4(wgs84, utm, p));

    const fieldRect = this.getGeometries()?.fieldRectangle;
    const targetSource = (fieldRect && fieldRect.length >= 4) ? fieldRect : this.rect;
    const targetUTM = centroidUTM(targetSource, wgs84, utm);

    this.layoutPoints.forEach((lp, i) => {
      const mast = points[i];
      if (!mast) return;

      const mastUTM = proj4(wgs84, utm, mast);
      const legacyDir = legacyEdgeDirection(rectUTM, lp);

      const toCentre = [targetUTM[0] - mastUTM[0], targetUTM[1] - mastUTM[1]];
      const dist = Math.hypot(toCentre[0], toCentre[1]);
      if (dist < 1e-6) {
        lp.angleOffset = 0;
        return;
      }
      const centreDir = [toCentre[0] / dist, toCentre[1] / dist];
      lp.angleOffset = signedAngleDeg(centreDir, legacyDir);
    });
  }

 // ── Ausrichtungspfeile ───────────────────────────────────────────────
 // 2026-08-05 (Signify-Feedback: Dunkelflecken nach dem Verschieben von
 // Masten, Gut Heil Lübeck 85706-1).
 //
 // BISHER kam die Blickrichtung ausschließlich aus der Kantenrichtung des
 // Mastrechtecks, gedreht um den fest hinterlegten Layout-Winkel
 // (config/mastlayouts.js, z.B. 45° für Eckmasten). Die tatsächliche
 // Mastposition ging nur als STARTPUNKT des Pfeils ein, nie als Richtung.
 // Zieht man einen Mast aus seiner Preset-Position, zielt der Pfeil also
 // weiter dorthin, wo der Mast vorher stand — und der Optimierer darf ihn
 // hinterher nur noch um ±12,5° um diese veraltete Nullrichtung drehen.
 // Ergebnis: dunkle Ecken, die der GA gar nicht beheben KANN, weil sein
 // Suchfenster um die falsche Mitte kreist.
 //
 // JETZT zielt der Pfeil auf den Feldmittelpunkt. Der Layout-Winkel bleibt
 // als Versatz erhalten: in der Preset-Position zeigt der Pfeil damit
 // praktisch dorthin, wo er vorher auch zeigte (der Layout-Winkel WAR ja so
 // gewählt, dass er ins Feld weist) — Bestandsprojekte verschieben sich
 // dadurch nicht spürbar. Sobald ein Mast wandert, wandert die Nullrichtung
 // aber mit.
 //
 // Gerechnet wird in UTM, nicht in lng/lat: bei 52° N sind 1° Länge ≈ 68 km
 // gegen 1° Breite ≈ 111 km, ein Winkel aus rohen Gradkoordinaten wäre um
 // bis zu ~35° verzerrt. Denselben Fehler hatten wir schon bei der
 // Rechteck-Normalisierung.
 // ── Gespeicherte Skizze übernehmen („Skizze bearbeiten", 07.08.2026) ──
 // Beim Laden eines gespeicherten Entwurfs fehlen Mastrechteck und
 // Layout-Raster (beides wird nie persistiert). Ohne sie hätte placeArrows
 // keine Basis und würde die Pfeile beim Verschieben eines Masten einfach
 // stehen lassen — genau der Dunkelfleck-Fehler, den der 05.08.-Fix behoben
 // hat, nur durch die Hintertür.
 //
 // Statt das Raster zu erraten, messen wir den Versatz aus den GESPEICHERTEN
 // Pfeilen: für jeden Mast, um wie viel seine Blickrichtung von "zur
 // Feldmitte" abweicht. Das ist dieselbe Größe, die _captureAngleOffsets in
 // der Preset-Lage bestimmt. Danach rechnet placeArrows unverändert weiter:
 // die Ausrichtungsabsicht des Entwurfs bleibt erhalten UND folgt dem Mast,
 // wenn er verschoben wird.
 //
 // Gibt true zurück, wenn übernommen wurde.
 // `raster` = die edges/t/angles des erkannten Layouts, dazu `preset: true`,
 // wenn es aus mastlayouts.js stammt (und nicht aus der Geometrie gemessen
 // wurde). Gebraucht wird es doppelt: die Schiene muss wissen, auf WELCHER
 // Kante ein Mast sitzt, und bei einem Preset koennen wir die Ausrichtung
 // nach der heutigen Regel neu bestimmen statt die gespeicherte zu messen.
 //
 // Rueckgabe: "preset" (Ausrichtung neu gerechnet — der Aufrufer sollte
 // placeArrows nachziehen), "gemessen" oder false.
 restoreFromSketch(rect, points, arrows, raster = null) {
  if (!rect?.length || !points?.length) return false;

  // ── Fall 1: Preset bekannt → Ausrichtung nach heutiger Regel ─────────
  // Die gespeicherten Pfeile sind die vom ZEICHNEN, nicht aus der
  // Berechnung (die dreht nur geometrylocal). Projekte von vor dem
  // 05.08.2026 tragen deshalb die alte Ausrichtung: Kantenrichtung des
  // Mastrechtecks plus fester Layoutwinkel — die zielt nicht auf die
  // Feldmitte, und der Optimierer darf sie danach nur um +/-12,5 Grad
  // drehen. Genau daher die Dunkelflecken.
  //
  // Beim Bearbeiten rechnen wir die Ausrichtung deshalb frisch: dieselbe
  // Messung wie beim Zeichnen (_captureAngleOffsets), also Blickrichtung
  // Feldmitte plus der im Layout gewollte Versatz. Ein bearbeitetes
  // Altprojekt startet damit von der korrigierten Nullrichtung.
  if (raster?.length === points.length && raster.every((r) => r && r.preset)) {
    this.rect = rect;
    this.layoutPoints = raster.map((r) => ({
      edge: r.edge, t: r.t, angle: r.angle, rasterEcht: true,
    }));
    this._captureAngleOffsets(points);
    return "preset";
  }

  const wgs84 = "EPSG:4326";
  const utm = getUtmProj(rect[0]);
  // Zielpunkt ist die SPIELFELD-Mitte, wie in placeArrows — nicht die Mitte
  // des uebergebenen Rechtecks (das ist das groessere Mastrechteck).
  const fieldRect = this.getGeometries()?.fieldRectangle;
  const targetUTM = centroidUTM(
    (fieldRect && fieldRect.length >= 4) ? fieldRect : rect, wgs84, utm
  );

  this.rect = rect;
  this.layoutPoints = points.map((mast, i) => {
    const arrow = arrows?.[i];
    let angleOffset = 0;
    if (arrow && arrow.length >= 2) {
      const mastUTM = proj4(wgs84, utm, mast);
      const endUTM = proj4(wgs84, utm, arrow[1]);
      const aim = [endUTM[0] - mastUTM[0], endUTM[1] - mastUTM[1]];
      const toCentre = [targetUTM[0] - mastUTM[0], targetUTM[1] - mastUTM[1]];
      const aimLen = Math.hypot(aim[0], aim[1]);
      const cLen = Math.hypot(toCentre[0], toCentre[1]);
      if (aimLen > 1e-6 && cLen > 1e-6) {
        angleOffset = signedAngleDeg(
          [toCentre[0] / cLen, toCentre[1] / cLen],
          [aim[0] / aimLen, aim[1] / aimLen],
        );
      }
    }
    // edge/t/angle aus dem erkannten Layout übernehmen. Ohne Layout bleibt
    // Kante 0 als Notnagel stehen, damit legacyEdgeDirection in placeArrows
    // nicht auf undefined läuft (greift nur, wenn ein Mast exakt auf der
    // Feldmitte liegt) — die Schiene ist dann allerdings unzuverlässig und
    // wird in aufSchiene deshalb übersprungen.
    const r = raster?.[i];
    return {
      edge: r ? r.edge : 0,
      t: r ? r.t : 0,
      angle: r ? r.angle : 0,
      rasterEcht: Boolean(r),
      angleOffset,
    };
  });
  return "gemessen";
 }

 // ── Schiene: Masten bleiben auf dem Mastrechteck ──────────────────────
 // (Philip 10.08.2026) Der Cursor wird auf das Rechteck projiziert, der Mast
 // rutscht also an der gestrichelten Linie entlang statt frei im Gelände zu
 // landen.
 //
 // Projiziert wird auf die NÄCHSTGELEGENE der vier Kanten, nicht nur auf die
 // eigene: sonst käme man mit einem Mast nie auf die Nachbarseite, obwohl
 // genau das ein üblicher Entwurfsschritt ist (Philip 10.08.). Wechselt ein
 // Mast die Seite, schreiben wir Kante und Parameter mit, damit die weiteren
 // Züge zur neuen Seite passen.
 //
 // Gerechnet wird in UTM. In rohen Grad wäre die Projektion verzerrt (bei
 // 52° N sind 1° Länge ≈ 68 km gegen 1° Breite ≈ 111 km), der Mast würde
 // schräg neben der Linie landen — derselbe Fallstrick wie bei der
 // Rechteck-Normalisierung und der Mittellinie.
 //
 // Ohne Rechteck oder ohne echtes Kantenraster (Projekt ohne zuordenbares
 // Layout) bleibt es beim freien Ziehen — lieber frei als auf der falschen
 // Kante.
 aufSchiene(index, lngLatArr) {
  const lp = this.layoutPoints?.[index];
  if (!this.rect || !lp || lp.rasterEcht === false) return lngLatArr;

  const wgs84 = "EPSG:4326";
  const utm = getUtmProj(this.rect[0]);
  const ecken = [0, 1, 2, 3].map((i) => proj4(wgs84, utm, this.rect[i]));
  const P = proj4(wgs84, utm, lngLatArr);

  let beste = null;
  for (let e = 0; e < 4; e++) {
    const A = ecken[e];
    const B = ecken[(e + 1) % 4];
    const vec = [B[0] - A[0], B[1] - A[1]];
    const len2 = vec[0] * vec[0] + vec[1] * vec[1];
    if (len2 < 1e-9) continue;

    // Lotfußpunkt, auf die Strecke begrenzt (wie projectPointOntoLine in
    // geometry.js, dort aber ohne Begrenzung).
    let t = ((P[0] - A[0]) * vec[0] + (P[1] - A[1]) * vec[1]) / len2;
    t = Math.max(0, Math.min(1, t));
    const X = [A[0] + t * vec[0], A[1] + t * vec[1]];
    const d = Math.hypot(X[0] - P[0], X[1] - P[1]);
    if (!beste || d < beste.d) beste = { d, e, t, X };
  }
  if (!beste) return lngLatArr;

  lp.edge = beste.e;
  lp.t = beste.t;
  return proj4(utm, wgs84, beste.X);
 }

 placeArrows(points) {
  if (!this.rect || !this.layoutPoints) return;

  const rect = this.rect;
  const layoutPoints = this.layoutPoints;
  const wgs84 = "EPSG:4326";
  const utm = getUtmProj(rect[0]);
  const rectUTM = rect.map((p) => proj4(wgs84, utm, p));

  // Zielpunkt = Schwerpunkt des SPIELFELDS (nicht des Mastrechtecks — die
  // beiden fallen nicht zusammen, das Mastrechteck ist das größere).
  // Fallback auf das Mastrechteck, falls das Feld noch nicht gezeichnet ist.
  const fieldRect = this.getGeometries()?.fieldRectangle;
  const targetSource = (fieldRect && fieldRect.length >= 4) ? fieldRect : rect;
  const targetUTM = centroidUTM(targetSource, wgs84, utm);

  const arrows = layoutPoints.map((lp, i) => {
    const mast = points[i];
    if (!mast) return null;

    const mastUTM = proj4(wgs84, utm, mast);

    // Nullrichtung: vom Mast zur Feldmitte.
    const toCentre = [targetUTM[0] - mastUTM[0], targetUTM[1] - mastUTM[1]];
    const dist = Math.hypot(toCentre[0], toCentre[1]);

    let dir;
    if (dist < 1e-6) {
      // Mast liegt exakt auf der Feldmitte — physikalisch unsinnig (das
      // Warnbanner meldet das ohnehin), aber wir dürfen hier nicht durch
      // null teilen. Alte Berechnung als Notnagel.
      dir = legacyEdgeDirection(rectUTM, lp);
    } else {
      const centreDir = [toCentre[0] / dist, toCentre[1] / dist];
      // Layout-Winkel als Versatz auf die Richtung zur Feldmitte. In der
      // Preset-Position ist das nahezu deckungsgleich mit der alten
      // Berechnung; nach dem Verschieben folgt die Richtung dem Mast.
      dir = rotate2D(centreDir, (lp.angleOffset ?? 0) * Math.PI / 180);
    }

    const L = 15;
    const endUTM = [mastUTM[0] + dir[0] * L, mastUTM[1] + dir[1] * L];

    return [mast, proj4(utm, wgs84, endUTM)];
  }).filter(Boolean);

  this.setGeometries((prev) => ({ ...prev, mastArrows: arrows }));

  return arrows;
}

reset() {
  if (this.map.getSource("mast-points")) {
    this.map.getSource("mast-points").setData({ type: "FeatureCollection", features: [] });
  }
  if (this.map.getSource("mast-layout-arrows")) {
    this.map.getSource("mast-layout-arrows").setData({ type: "FeatureCollection", features: [] });
  }
  this.rect = null;
  this.layoutPoints = null;
  this.selectedIndex = null;
  this.fieldWarning.style.display = "none";
}

 onClick(lngLat) {
  const points = this.getGeometries().mastPoints;
  if (!points?.length) return;

  const idx = this.findNearestPointIndex(lngLat, points);

  if (this.selectedIndex !== null && idx === null) {
    this.selectedIndex = null;
    this.setGeometries(prev => ({...prev, mastPoints: [...points], selectedMastIndex: null}));
    return;
  }

  if (idx === this.selectedIndex) {
    this.selectedIndex = null;
    this.setGeometries(prev => ({...prev, mastPoints: [...points], selectedMastIndex: null}));
    return;
  }

  if (idx !== null) {
    // Lage VOR der Verschiebung sichern (der Mast folgt ab jetzt der Maus)
    this.history.push(points.map((p) => [...p]));
    if (this.history.length > 30) this.history.shift();
    this.selectedIndex = idx;
    this.setGeometries(prev => ({...prev, mastPoints: [...points], selectedMastIndex: null}));
  }
}

// Letzte Mast-Verschiebung zurücknehmen. Stellt den Schnappschuss von
// vor dem letzten Anwählen wieder her (inkl. Richtungspfeile).
undo() {
  if (!this.history.length) return false;
  const points = this.history.pop();
  this.selectedIndex = null;
  const arrows = this.placeArrows(points);
  this.setGeometries((prev) => ({
    ...prev,
    mastPoints: points,
    mastArrows: arrows || prev.mastArrows,
    selectedMastIndex: null,
  }));
  this.updateFieldWarning(points);
  return true;
}

 onMouseMove(lngLat) {
  if (this.selectedIndex === null) return;

  const points = this.getGeometries().mastPoints;
  const updated = [...points];

  // Nicht die rohe Cursorposition: der Mast bleibt auf seiner Kante.
  updated[this.selectedIndex] = this.aufSchiene(
    this.selectedIndex, [lngLat.lng, lngLat.lat]
  );

  const arrows = this.placeArrows(updated);
  // arrows kann null sein (kein Raster vorhanden) — dann die bisherigen
  // Pfeile behalten statt sie zu löschen. Gleiche Absicherung wie in undo().
  this.setGeometries(prev => ({...prev, mastPoints: updated, mastArrows: arrows || prev.mastArrows}));
  // Live-Warnung während des Ziehens
  this.updateFieldWarning(updated);
}

  findNearestPointIndex(lngLat, points) {
    let min = Infinity;
    let idx = null;

    points.forEach((p, i) => {
      const d = Math.hypot(p[0] - lngLat.lng, p[1] - lngLat.lat);
      if (d < min && d < 0.00002) {
        min = d;
        idx = i;
      }
    });

    return idx;
  }
}

function rotate2D([x, y], a) {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return [x * c - y * s, x * s + y * c];
}

// Schwerpunkt eines Polygons (Eckenmittel), in UTM-Metern. Für ein Rechteck
// ist das exakt der Mittelpunkt. Das Rechteck kommt aus MapLibre gelegentlich
// geschlossen (letzter Punkt = erster) — der Duplikatpunkt würde den
// Schwerpunkt zu dieser Ecke ziehen, deshalb fliegt er raus.
function centroidUTM(polygonLngLat, wgs84, utm) {
  const pts = polygonLngLat.map((p) => proj4(wgs84, utm, p));
  const first = pts[0];
  const last = pts[pts.length - 1];
  const ring = (pts.length > 1 &&
                Math.abs(first[0] - last[0]) < 1e-9 &&
                Math.abs(first[1] - last[1]) < 1e-9)
    ? pts.slice(0, -1)
    : pts;
  const sum = ring.reduce((acc, p) => [acc[0] + p[0], acc[1] + p[1]], [0, 0]);
  return [sum[0] / ring.length, sum[1] / ring.length];
}

// Die BISHERIGE Richtungsberechnung: Kantenrichtung des Mastrechtecks,
// gedreht um den Layout-Winkel. Wird noch an zwei Stellen gebraucht — zum
// Messen des Versatzes in der Preset-Lage und als Notnagel, wenn ein Mast
// exakt auf der Feldmitte liegt.
function legacyEdgeDirection(rectUTM, lp) {
  const A = rectUTM[lp.edge];
  const B = rectUTM[(lp.edge + 1) % 4];
  const edgeVec = [B[0] - A[0], B[1] - A[1]];
  const len = Math.hypot(edgeVec[0], edgeVec[1]);
  const edgeDir = len ? [edgeVec[0] / len, edgeVec[1] / len] : [1, 0];
  return rotate2D(edgeDir, ((lp.angle - 90) * Math.PI) / 180);
}

// Vorzeichenbehafteter Winkel von a nach b, in Grad (-180..180).
// atan2 auf Kreuz- und Skalarprodukt — numerisch stabiler als acos, das bei
// nahezu parallelen Vektoren an der Rundung stirbt.
function signedAngleDeg(a, b) {
  const cross = a[0] * b[1] - a[1] * b[0];
  const dot = a[0] * b[0] + a[1] * b[1];
  return (Math.atan2(cross, dot) * 180) / Math.PI;
}

// Punkt-in-Polygon per Ray-Casting, direkt in lng/lat (Feld ist klein
// genug, Verzerrung spielt für den Drin/Draußen-Test keine Rolle).
// rect = die 4 Ecken des Feld-Rechtecks (beliebig rotiert).
function pointInPolygon([px, py], polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}